/**
 * Tabs, in the two forms the product uses.
 *
 * `TabLinks` when each tab is its own page (a route): real links, so the URL
 * says where you are and cmd-click works. `TabButtons` when the tabs switch
 * what one page shows. Both are a labeled <nav> of pill buttons with the
 * current one filled; the label is the landmark's accessible name.
 */
import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import styles from './Tabs.module.css';

const cls = (on: boolean) => (on ? `${styles.tab} ${styles.tabOn}` : styles.tab);

export function TabLinks({ label, items }: {
  label: string;
  items: { to: string; label: ReactNode }[];
}) {
  return (
    <nav className={styles.tabs} aria-label={label}>
      {items.map((item) => (
        <NavLink key={item.to} to={item.to} className={({ isActive }) => cls(isActive)}>
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

export function TabButtons<K extends string>({ label, items, current, onSelect }: {
  label: string;
  items: { key: K; label: ReactNode }[];
  current: K;
  onSelect: (key: K) => void;
}) {
  return (
    <nav className={styles.tabs} aria-label={label}>
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          aria-pressed={current === item.key}
          className={cls(current === item.key)}
          onClick={() => onSelect(item.key)}
        >
          {item.label}
        </button>
      ))}
    </nav>
  );
}
